<script lang="ts">
  import { createEventDispatcher, tick } from 'svelte';
  import type { GuestTrialAccent } from '../../js/guestTrialProducts';

  export let show = false;
  export let productName = '';
  export let productTagline = '';
  export let icon = 'lightbulb';
  export let accent: GuestTrialAccent = 'teal';

  const dispatch = createEventDispatcher<{
    continue: void;
    discard: void;
  }>();

  let dialogEl: HTMLDivElement | null = null;
  let previouslyFocused: Element | null = null;

  const FOCUSABLE =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function accentVar(shade: 'base' | 'soft' | 'ink' = 'base'): string {
    const suffix = shade === 'base' ? '' : `-${shade}`;
    return `var(--accent-${accent}${suffix})`;
  }

  function handleContinue(): void {
    dispatch('continue');
    show = false;
  }

  function handleDiscard(): void {
    dispatch('discard');
    show = false;
  }

  function handleKeydown(event: KeyboardEvent): void {
    if (!show) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      handleDiscard();
      return;
    }
    if (event.key === 'Tab' && dialogEl) {
      const focusable = dialogEl.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  $: if (typeof document !== 'undefined') {
    if (show) {
      previouslyFocused = document.activeElement;
      document.body.style.overflow = 'hidden';
      tick().then(() => {
        if (!dialogEl) return;
        const primary = dialogEl.querySelector<HTMLElement>('[data-autofocus]');
        const first = primary || dialogEl.querySelector<HTMLElement>(FOCUSABLE);
        if (first) first.focus();
        else dialogEl.focus();
      });
    } else {
      document.body.style.overflow = '';
      if (previouslyFocused && previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
        previouslyFocused = null;
      }
    }
  }
</script>

<svelte:window on:keydown={handleKeydown} />

{#if show}
  <div
    class="resume-backdrop"
    role="presentation"
    on:click|self={handleDiscard}
    on:keydown|self={(e) => {
      if (e.key === 'Enter' || e.key === ' ') handleDiscard();
    }}
  >
    <div
      class="resume-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="guest-trial-resume-title"
      aria-describedby="guest-trial-resume-desc"
      tabindex="-1"
      bind:this={dialogEl}
      style="--resume-accent: {accentVar()}; --resume-accent-soft: {accentVar('soft')}; --resume-accent-ink: {accentVar('ink')};"
    >
      <div class="resume-hero" aria-hidden="true">
        <span class="resume-orb resume-orb--a"></span>
        <span class="resume-orb resume-orb--b"></span>
      </div>

      <div class="resume-body">
        <span class="resume-chip">Free trial</span>

        <span class="resume-icon-chip" aria-hidden="true">
          <i class="{icon} icon"></i>
        </span>

        <h2 id="guest-trial-resume-title" class="resume-title">Pick up where you left off</h2>

        <p id="guest-trial-resume-desc" class="resume-lead">
          You started a <strong>{productName}</strong> trial before signing in. Your progress is saved and ready to
          continue.
        </p>

        {#if productTagline}
          <p class="resume-tagline">{productTagline}</p>
        {/if}
      </div>

      <div class="resume-actions">
        <button type="button" class="resume-btn resume-btn--secondary" on:click={handleDiscard}>
          Start fresh
        </button>
        <button
          type="button"
          class="resume-btn resume-btn--primary"
          data-autofocus
          on:click={handleContinue}
        >
          Continue trial
          <i class="arrow right icon" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .resume-backdrop {
    position: fixed;
    inset: 0;
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-4);
    background: color-mix(in srgb, var(--grey) 45%, transparent);
    backdrop-filter: blur(4px);
  }

  .resume-dialog {
    position: relative;
    width: min(100%, 28rem);
    background: var(--white);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow-pop);
    overflow: hidden;
    animation: resume-enter 0.28s ease-out;
  }

  @keyframes resume-enter {
    from {
      opacity: 0;
      transform: translateY(12px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .resume-hero {
    position: relative;
    height: 5.5rem;
    background: linear-gradient(
      135deg,
      var(--resume-accent-soft) 0%,
      color-mix(in srgb, var(--resume-accent-soft) 55%, var(--white)) 100%
    );
    border-bottom: 1px solid color-mix(in srgb, var(--resume-accent) 12%, transparent);
    overflow: hidden;
  }

  .resume-orb {
    position: absolute;
    border-radius: 50%;
    background: color-mix(in srgb, var(--resume-accent) 18%, transparent);
  }

  .resume-orb--a {
    width: 7rem;
    height: 7rem;
    top: -2.5rem;
    right: -1.5rem;
  }

  .resume-orb--b {
    width: 4.5rem;
    height: 4.5rem;
    bottom: -2rem;
    left: 1.5rem;
    background: color-mix(in srgb, var(--resume-accent) 10%, transparent);
  }

  .resume-body {
    padding: 0 var(--space-6) var(--space-5);
    margin-top: calc(-1 * var(--space-6));
    text-align: left;
  }

  .resume-chip {
    display: inline-flex;
    align-items: center;
    padding: 0.35rem 0.85rem;
    margin-bottom: var(--space-4);
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--resume-accent-ink);
    background: var(--resume-accent-soft);
    border: 1px solid color-mix(in srgb, var(--resume-accent) 22%, transparent);
    border-radius: var(--radius-pill);
  }

  .resume-icon-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 3.25rem;
    height: 3.25rem;
    margin-bottom: var(--space-4);
    border-radius: var(--radius-card);
    background: var(--resume-accent-soft);
    color: var(--resume-accent-ink);
    box-shadow: 0 4px 14px color-mix(in srgb, var(--resume-accent) 18%, transparent);
  }

  .resume-icon-chip :global(i.icon) {
    margin: 0;
    font-size: 1.35rem;
  }

  .resume-title {
    margin: 0 0 var(--space-3);
    font-size: clamp(1.35rem, 3vw, var(--text-2xl));
    font-weight: 600;
    line-height: 1.25;
    color: var(--grey);
  }

  .resume-lead {
    margin: 0 0 var(--space-3);
    color: var(--grey-secondary);
    line-height: 1.6;
  }

  .resume-lead strong {
    color: var(--grey);
    font-weight: 500;
  }

  .resume-tagline {
    margin: 0;
    padding: var(--space-3) var(--space-4);
    font-size: var(--text-sm);
    line-height: 1.5;
    color: var(--resume-accent-ink);
    background: color-mix(in srgb, var(--resume-accent-soft) 80%, var(--white));
    border-left: 3px solid var(--resume-accent);
    border-radius: 0 var(--radius-input) var(--radius-input) 0;
  }

  .resume-actions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-3);
    padding: var(--space-4) var(--space-6) var(--space-6);
    border-top: 1px solid var(--border-color);
    background: var(--greyish);
  }

  .resume-btn {
    flex: 1 1 9rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    min-height: 2.75rem;
    padding: 0.65rem 1rem;
    border: 0;
    border-radius: var(--radius-cta);
    font: inherit;
    font-weight: 500;
    cursor: pointer;
    box-shadow: var(--shadow-cta);
    transition:
      background-color 0.2s ease,
      color 0.2s ease,
      box-shadow 0.2s ease,
      transform 0.15s ease;
  }

  .resume-btn:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  .resume-btn--primary {
    background: var(--primary);
    color: var(--white);
  }

  .resume-btn--primary:hover {
    background: var(--darkprimary);
  }

  .resume-btn--primary :global(i.icon) {
    margin: 0;
    font-size: 0.9em;
  }

  .resume-btn--secondary {
    background: var(--white);
    color: var(--grey-secondary);
    border: 1px solid var(--border-color);
    box-shadow: none;
  }

  .resume-btn--secondary:hover {
    color: var(--grey);
    background: var(--greyish);
  }

  .resume-dialog:focus {
    outline: none;
  }

  @media (max-width: 480px) {
    .resume-actions {
      flex-direction: column-reverse;
    }

    .resume-btn {
      width: 100%;
      flex-basis: auto;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .resume-dialog {
      animation: none;
    }
  }
</style>
